import React, { useEffect, useState } from "react";
import Loader from "../../components/Loader";
import ThemedTable from "../../components/ThemedTable";
import { useNavigate } from "react-router-dom";
import { Phone, Mail, Eye, Trash2, Grid3x3, List, Plus } from "lucide-react";
import { apiClient } from "../../apiclient/apiclient";
import { toast } from "react-toastify";
import ConfirmDialog from "../../components/ConfirmDialog";
const AllAdmins = () => {
  const [admins, setAdmins] = useState([]);
  const [failedImages, setFailedImages] = useState({});
  const [view, setView] = useState("cards"); // cards OR list
  const [loading, setLoading] = useState(true);
  const [deleteUser, setDeleteUser] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  const base = (process.env.REACT_APP_IMAGE_URL || "http://localhost:5000")
    .replace(/\/uploads\/?$/i, '')
    .replace(/\/$/, "");


  // Fetch all users from backend
  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get("/all");
      setAdmins(response.data.users || []);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to fetch users");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Delete user
  const handleConfirmDelete = async () => {
    if (!deleteUser) return;

    try {
      setDeleting(true);

      await apiClient.delete(`/user/${deleteUser._id}`);

      toast.success("User deleted successfully");

      setAdmins(prev => prev.filter(a => a._id !== deleteUser._id));
      setDeleteUser(null);

    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete user");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <Loader size={40} />
      </div>
    );
  }
  return (
    <div>
      <div className="w-full border-b pt-3 ps-4 pb-4 border-slate-200 bg-blue-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-0">
        <div>
          <h1 className="text-xl font-bold text-slate-800">All Users</h1>
          <p className="text-sm text-slate-500 mt-1">Manage all users in the system</p>
        </div>
        <div className="flex gap-3 items-center mt-2 sm:mt-0">
          <button
            onClick={() => navigate("/profile/add")}
            className="px-3 py-2 rounded-md bg-orange-500 text-white hover:bg-orange-600 transition flex items-center justify-center"
            title="Add User"
          >
            <Plus className="w-5 h-5" />
          </button>
          <button
            onClick={() => setView("cards")}
            className={`px-3 py-2 rounded-md transition flex items-center justify-center ${view === "cards" ? "bg-orange-500 text-white" : "bg-orange-500 text-white hover:bg-orange-600"}`}
            title="Card View"
          >
            <Grid3x3 className="w-5 h-5" />
          </button>
          <button
            onClick={() => setView("list")}
            className={`px-3 me-4 py-2 rounded-md transition flex items-center justify-center ${view === "list" ? "bg-orange-500 text-white" : "bg-orange-500 text-white hover:bg-orange-600"}`}
            title="List View"
          >
            <List className="w-5 h-5" />
          </button>
        </div>
      </div>



      {/* Show NO DATA message */}
      {admins.length === 0 ? (
        <div className="text-gray-500 text-lg">
          {loading ? "Loading..." : "No admins found. Add from Add Admin page."}
        </div>
      ) : (
        <>
          {/* CARD VIEW */}
          {view === "cards" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-0 sm:gap-0 xl:gap-0">
              {admins.map((admin, idx) => {
                const key = admin._id || idx;
                const failed = !!failedImages[key];

                return (
                  <div
                    key={key}
                    className="relative group bg-white border-b border-e p-2
flex flex-col items-center transition-all duration-200 "
                  >
                    {/* Delete Button - Top Right Corner */}
                    <button
                      onClick={() => setDeleteUser(admin)}
                      className="absolute top-3 right-3 p-2 bg-orange-500 text-white rounded-full hover:bg-orange-600 transition z-30 opacity-0 group-hover:opacity-100"
                      title="Delete user"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>

                    {/* Hover overlay with Eye */}
                    <div className="absolute inset-0 bg-white/50 backdrop-blur-[2px]
opacity-0 group-hover:opacity-100
flex justify-center items-center
transition z-20">
                      <div className="relative">
                        <button
                          onClick={() => navigate(`/profile/view/${key}`, { state: { admin } })}
                          className="w-10 h-10 flex items-center justify-center"
                          aria-label={`View ${admin.name}`}
                        >
                          <Eye className="w-8 h-8 text-black rounded-full" />
                        </button>
                      </div>
                    </div>

                    {/* Profile Image */}
                    <div className="w-24 h-24 sm:w-32 sm:h-32 mt-2 rounded-full overflow-hidden border shadow-sm bg-gray-100 flex items-center justify-center relative z-10">
                      {admin.image && !failed ? (
                        <img
                          src={`${base}${admin.image}`}
                          alt="profile"
                          className="object-cover w-full h-full"
                          onError={() =>
                            setFailedImages((prev) => ({
                              ...prev,
                              [key]: true,
                            }))
                          }
                        />
                      ) : (
                        (() => {
                          const nameparts = (admin.name || "").trim().split(" ");
                          const first = nameparts[0] ? nameparts[0].charAt(0).toUpperCase() : "";
                          const last = nameparts[1] ? nameparts[1].charAt(0).toUpperCase() : "";
                          const initials = first + last || first || "?";
                          return (
                            <div className="w-full h-full flex items-center justify-center bg-[#172554] text-white text-2xl font-semibold">
                              {initials}
                            </div>
                          );
                        })()
                      )}
                    </div>

                    {/* Name */}
                    <h2 className="text-lg sm:text-xl font-semibold mt-1 sm:mt-1 text-center">
                      {admin.name}
                    </h2>

                    <span
                      className={`mt-1 text-xs px-3 py-1 rounded-full font-medium
  ${admin.role === "superAdmin"
                          ? "bg-purple-100 text-purple-700"
                          : admin.role === "admin"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-emerald-100 text-emerald-700"
                        }`}
                    >
                      {admin.role || "Admin"}
                    </span>


                    {/* Email */}
                    <div className="mt-3 w-full">
                      <div className="bg-gray-50 px-3 py-2 rounded-md text-gray-700 text-sm flex gap-2 items-center break-all">
                        <Mail className="w-4 h-4 text-gray-500" />
                        {admin.email}
                      </div>
                    </div>

                    {/* Phone */}
                    <div className="mt-2 w-full">
                      <div className="bg-gray-50 px-3 py-2 rounded-md text-gray-700 text-sm flex gap-2 items-center break-all">
                        <Phone className="w-4 h-4 text-gray-500" />
                        {admin.phone || "N/A"}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* LIST VIEW */}

          {view === "list" && (
            <div className="bg-white p-0 sm:p-0 w-full overflow-x-auto">
              <ThemedTable className="text-left border-collapse w-full min-w-[700px]">
                <thead>
                  <tr className="bg-white text-gray-700">
                    <th className="p-3 whitespace-nowrap">Profile</th>
                    <th className="p-3 whitespace-nowrap">Name</th>
                    <th className="p-3 whitespace-nowrap">Email</th>
                    <th className="p-3 whitespace-nowrap">Phone</th>
                    <th className="p-3 whitespace-nowrap">Role</th>
                    <th className="p-3 whitespace-nowrap text-center">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {admins.map((admin, idx) => {
                    const key = admin._id || idx;
                    const failed = !!failedImages[key];

                    return (
                      <tr key={key} className="border-b hover:bg-gray-50">
                        <td className="p-2">
                          {admin.image && !failed ? (
                            <img
                              src={`${base}${admin.image}`}
                              alt="Profile"
                              className="w-10 h-10 rounded-full object-cover"
                              onError={() =>
                                setFailedImages((prev) => ({
                                  ...prev,
                                  [key]: true,
                                }))
                              }
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-[#172554] text-white flex items-center justify-center text-sm font-semibold">
                              {(admin.name?.[0] || "?").toUpperCase()}
                            </div>
                          )}
                        </td>

                        <td className="p-3 whitespace-nowrap">{admin.name}</td>
                        <td className="p-3 whitespace-nowrap">{admin.email}</td>
                        <td className="p-3 whitespace-nowrap">
                          {admin.phone || "N/A"}
                        </td>

                        <td className="p-3 whitespace-nowrap">
                          <span className="px-3 py-1 bg-gray-100 rounded text-gray-700">
                            {admin.role}
                          </span>
                        </td>

                        <td className="p-3 whitespace-nowrap">
                          <div className="flex gap-2">
                            <button
                              onClick={() =>
                                navigate(`/profile/view/${key}`, {
                                  state: { admin },
                                })
                              }
                              className="p-2 rounded-full border-2 border-[#172554] text-[#172554] hover:bg-[#172554] hover:text-white transition"
                            >
                              <Eye className="w-4 h-4" />
                            </button>

                            <button
                              onClick={() => setDeleteUser(admin)}
                              className="p-2 rounded-full border-2 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white transition"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </ThemedTable>
            </div>

          )}

        </>
      )}
      <ConfirmDialog
        open={!!deleteUser}
        title="Delete User"
        description={`Are you sure you want to delete ${deleteUser?.name}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        loading={deleting}
        onCancel={() => setDeleteUser(null)}
        onConfirm={handleConfirmDelete}
      />
    </div>

  );
};

export default AllAdmins;
